import { Suspense } from 'react';

import { Group, Skeleton, Stack, useMantineTheme } from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../../../../../shared/components/UI/Button/Button';
import { EmptyPlaceholder } from '../../../../../../../shared/components/UI/EmptyPlaceholder/EmptyPlaceholder';

import * as columnsDefinition from '../utils/columnsDefinition';
import { GradientBorder } from '../../../../../../../shared/components/GradientBorder';
import { useGetAllResponseProject } from '../../../hooks/useGetAllResponseProject.hooks';
import type { ProjectTableData } from '../utils/tableConfig';
import { getProjectResponseTableConfig } from '../utils/tableConfig';

interface ResponseProjectTableProps {
  onCreateProject?: () => void;
}

export function ResponseProjectTable(props: ResponseProjectTableProps) {
  return (
    <Suspense fallback={<Loading />}>
      <SafeResponseProjectTable {...props} />
    </Suspense>
  );
}

function Loading() {
  return (
    <Stack w="100%" h="100%" spacing="03" p="04">
      <Group w="100%" spacing="01" noWrap>
        <Skeleton h={15} w="70%" />
        <Skeleton h={15} w="15%" />
        <Skeleton h={15} w="15%" />
      </Group>
      {Array.from({ length: 5 }).map((_, index) => (
        <Group w="100%" spacing="01" key={index} noWrap>
          <Skeleton h={40} w="70%" />
          <Skeleton h={40} w="15%" />
          <Skeleton h={40} w="15%" />
        </Group>
      ))}
    </Stack>
  );
}

function SafeResponseProjectTable({
  onCreateProject,
}: ResponseProjectTableProps) {
  const { data } = useGetAllResponseProject();
  const theme = useMantineTheme();
  const { t } = useTranslation('bidResponse');
  const navigate = useNavigate();

  const onRowClick = (row: ProjectTableData) => {
    navigate(`/response/projects/${row.id}`);
  };

  const tableConfig = getProjectResponseTableConfig(t, { onRowClick });

  const columns = [
    columnsDefinition.bidResponseTitle(t),
    columnsDefinition.responseDeadline(t),
    columnsDefinition.action(t),
  ];

  const table = useMantineReactTable({
    columns,
    data: data.projects,
    ...tableConfig,
  });

  if (data.projects.length === 0) {
    return <Empty onCreateProject={onCreateProject} />;
  }

  return (
    <GradientBorder
      borderRadius={12} // todo: use theme.spacing.["02"]
      borderWidth={1}
      gradient={theme.other.gradients.background2}
    >
      <Stack p="04" w="100%">
        <MantineReactTable table={table} />
      </Stack>
    </GradientBorder>
  );
}

function Empty({
  onCreateProject,
}: Pick<ResponseProjectTableProps, 'onCreateProject'>) {
  const { t } = useTranslation('bidResponse');

  return (
    <EmptyPlaceholder
      title={t('project.projectTable.emptyPlaceholder.title')}
      subtitle={t('project.projectTable.emptyPlaceholder.subtitle')}
      actionsComponent={
        onCreateProject && (
          <Button variant="light" onClick={onCreateProject}>
            {t('project.projectTable.emptyPlaceholder.cta')}
          </Button>
        )
      }
    />
  );
}
